import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Tag } from "lucide-react";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { fetchPublishedBlogBySlug } from "../../utils/blogApi";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1504711434969-e33886168d8c?auto=format&fit=crop&w=1200&q=80";

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadArticle = async () => {
      if (!slug) {
        setError("Invalid article URL");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError("");

      try {
        const response = await fetchPublishedBlogBySlug(slug);
        if (!isMounted) {
          return;
        }
        setArticle(response);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }
        setError(loadError.message || "Failed to load article");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadArticle();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0">
      <Header />
      <main className="pt-24 sm:pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/busimag"
            className="inline-flex items-center gap-2 text-sm font-semibold text-violet-600 hover:text-violet-700"
          >
            <ArrowLeft className="w-4 h-4" /> Back to BusiMag
          </Link>

          {isLoading && (
            <div className="mt-8 rounded-2xl glass p-6 text-sm text-slate-500">
              Loading article...
            </div>
          )}

          {!isLoading && error && (
            <div className="mt-8 rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
              {error}
            </div>
          )}

          {!isLoading && !error && article && (
            <article className="mt-8">
              <div className="rounded-3xl overflow-hidden shadow-apple-xl">
                <img
                  src={article.image || FALLBACK_IMAGE}
                  alt={article.title}
                  className="w-full h-[260px] sm:h-[380px] object-cover"
                />
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3 text-xs">
                <span className="px-3 py-1.5 rounded-full glass text-slate-700 font-semibold">
                  {article.category}
                </span>
                <span className="inline-flex items-center gap-1.5 text-slate-500">
                  <Clock className="w-3.5 h-3.5" /> {article.readTime}
                </span>
                <span className="text-slate-500">{article.date}</span>
                <span className="text-slate-500">By {article.author}</span>
              </div>

              <h1 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
                {article.excerpt}
              </p>

              {Array.isArray(article.tags) && article.tags.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700"
                    >
                      <Tag className="w-3 h-3" /> {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-8 rounded-3xl bg-white p-6 sm:p-8 shadow-apple border border-slate-100">
                <div className="prose prose-slate max-w-none whitespace-pre-wrap">
                  {article.content}
                </div>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ArticlePage;
